import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchManualResume, updateManualResume, fetchManualResumeLatex, updateManualResumeFromLatex } from '../api';
import EditableField from '../components/EditableField';
import ChatBot from '../components/ChatBot';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ManualResumeEditor = () => {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [resumeData, setResumeData] = useState(null);
  const [resumeName, setResumeName] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState('contact');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [visibleSections, setVisibleSections] = useState({
    contact: true,
    education: true,
    experience: true,
    projects: true,
    skills: true,
    certifications: false,
    honors: false,
    leadership: false
  });

  // Editor mode: 'clean' for structured WYSIWYG, 'raw' for LaTeX editing
  const [editorMode, setEditorMode] = useState('clean');
  const [latexContent, setLatexContent] = useState('');
  const [latexLoading, setLatexLoading] = useState(false);
  const [latexSaving, setLatexSaving] = useState(false);
  const [latexError, setLatexError] = useState(null);

  // Advanced formatting settings
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [formatting, setFormatting] = useState({
    spacing_profile: 'auto',  // 'auto', 'very_spacious', 'spacious', 'balanced', 'compact'
    font_size: 'auto'         // 'auto', '10pt', '10.5pt', '11pt'
  });

  // AI Assistant (ChatBot) state
  const [showChatBot, setShowChatBot] = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);

  useEffect(() => {
    loadResume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const loadResume = async () => {
    try {
      setLoading(true);
      const data = await fetchManualResume(resumeId, getToken());
      setResumeData(data.resume_data);
      setResumeName(data.resume_name);

      // Load formatting settings if available
      if (data.formatting) {
        setFormatting({
          spacing_profile: data.formatting.spacing_profile || 'auto',
          font_size: data.formatting.font_size || 'auto'
        });
      }

      setLoading(false);

      // Load PDF after resume data is loaded
      reloadPDF();
    } catch (err) {
      console.error('Failed to load resume:', err);
      alert('Failed to load resume');
      navigate('/dashboard');
    }
  };


  // Helper to get current formatting for API calls (convert 'auto' to null)
  const getFormattingForApi = () => {
    const apiFormatting = {};
    if (formatting.spacing_profile !== 'auto') {
      apiFormatting.spacing_profile = formatting.spacing_profile;
    }
    if (formatting.font_size !== 'auto') {
      apiFormatting.font_size = formatting.font_size;
    }
    return Object.keys(apiFormatting).length > 0 ? apiFormatting : null;
  };

  const updateResumeField = async (path, value) => {
    try {
      // Update local state
      const pathParts = path.split(/\.|\[|\]/).filter(Boolean);
      const newData = JSON.parse(JSON.stringify(resumeData));

      let current = newData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }
      current[pathParts[pathParts.length - 1]] = value;

      setResumeData(newData);

      // Save to backend with formatting settings
      await updateManualResume(resumeId, newData, getToken(), getFormattingForApi());

      // Reload PDF
      reloadPDF();
    } catch (err) {
      console.error('Failed to update resume:', err);
      alert('Failed to save changes');
    }
  };

  // Update formatting settings and regenerate PDF
  const updateFormatting = async (newFormatting) => {
    try {
      setFormatting(newFormatting);

      // Convert 'auto' to null for API
      const apiFormatting = {};
      if (newFormatting.spacing_profile !== 'auto') {
        apiFormatting.spacing_profile = newFormatting.spacing_profile;
      }
      if (newFormatting.font_size !== 'auto') {
        apiFormatting.font_size = newFormatting.font_size;
      }

      // Save with new formatting
      await updateManualResume(
        resumeId,
        resumeData,
        getToken(),
        Object.keys(apiFormatting).length > 0 ? apiFormatting : null
      );

      // Reload PDF with new formatting
      reloadPDF();
    } catch (err) {
      console.error('Failed to update formatting:', err);
      alert('Failed to apply formatting changes');
    }
  };

  const addBulletPoint = async (path) => {
    const pathParts = path.split(/\.|\[|\]/).filter(Boolean);
    const newData = JSON.parse(JSON.stringify(resumeData));

    let current = newData;
    for (let i = 0; i < pathParts.length; i++) {
      current = current[pathParts[i]];
    }

    current.push('New bullet point');
    setResumeData(newData);

    try {
      await updateManualResume(resumeId, newData, getToken(), getFormattingForApi());
      reloadPDF();
    } catch (err) {
      console.error('Failed to add bullet:', err);
    }
  };

  const deleteBulletPoint = async (path, index) => {
    if (!window.confirm('Delete this bullet point?')) return;

    const pathParts = path.split(/\.|\[|\]/).filter(Boolean);
    const newData = JSON.parse(JSON.stringify(resumeData));

    let current = newData;
    for (let i = 0; i < pathParts.length; i++) {
      current = current[pathParts[i]];
    }

    current.splice(index, 1);
    setResumeData(newData);

    try {
      await updateManualResume(resumeId, newData, getToken(), getFormattingForApi());
      reloadPDF();
    } catch (err) {
      console.error('Failed to delete bullet:', err);
    }
  };

  const addSection = (sectionType, sectionName) => {
    const newData = JSON.parse(JSON.stringify(resumeData));
    const blankTemplates = {
      education: { school_name: '', degree: '', major: '', graduation_date: '', gpa: null, coursework: [], honors: [] },
      experience: { company_name: '', job_title: '', location: '', start_date: '', end_date: '', bullets: [], technologies: [] },
      projects: { project_name: '', context: '', date: '', bullets: [], technologies: [] },
      certifications: { cert_name: '', issuing_org: '', date_obtained: '' },
      honors: { award_name: '', issuing_org: '', date: '' },
      leadership: { organization: '', role: '', dates: '', achievements: [] }
    };

    if (!newData[sectionName]) newData[sectionName] = [];
    newData[sectionName].push(blankTemplates[sectionType]);
    setResumeData(newData);

    updateManualResume(resumeId, newData, getToken(), getFormattingForApi()).catch(console.error);
  };

  const deleteSection = async (sectionName, index) => {
    if (!window.confirm('Delete this section?')) return;

    const newData = JSON.parse(JSON.stringify(resumeData));
    newData[sectionName].splice(index, 1);
    setResumeData(newData);

    try {
      await updateManualResume(resumeId, newData, getToken(), getFormattingForApi());
      reloadPDF();
    } catch (err) {
      console.error('Failed to delete section:', err);
    }
  };

  const toggleSection = (section) => {
    setVisibleSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Load LaTeX content when switching to raw mode
  const loadLatexContent = useCallback(async () => {
    try {
      setLatexLoading(true);
      setLatexError(null);
      const data = await fetchManualResumeLatex(resumeId, getToken());
      setLatexContent(data.latex_content);
    } catch (err) {
      console.error('Failed to load LaTeX:', err);
      setLatexError(err.message || 'Failed to load LaTeX content');
    } finally {
      setLatexLoading(false);
    }
  }, [resumeId, getToken]);

  // Handle editor mode switch
  const handleEditorModeChange = async (mode) => {
    if (mode === 'raw' && editorMode !== 'raw') {
      // Switching to raw mode - load LaTeX
      await loadLatexContent();
    } else if (mode === 'clean' && editorMode !== 'clean') {
      // Switching back to clean mode - reload resume data (in case LaTeX was saved)
      await loadResume();
    }
    setEditorMode(mode);
  };

  // Save LaTeX content
  const saveLatexContent = async () => {
    try {
      setLatexSaving(true);
      setLatexError(null);
      await updateManualResumeFromLatex(resumeId, latexContent, getToken());
      reloadPDF();
    } catch (err) {
      console.error('Failed to save LaTeX:', err);
      setLatexError(err.response?.data?.detail || err.message || 'Failed to save LaTeX. Check your syntax.');
    } finally {
      setLatexSaving(false);
    }
  };

  const reloadPDF = async () => {
    try {
      const timestamp = Date.now();
      const response = await fetch(`${API_URL}/resume/manual/${resumeId}/pdf?t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        console.error('Failed to load PDF:', response.statusText);
        setPdfUrl(null);
        return;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Revoke previous blob URL to prevent memory leaks
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrl);
      }

      setPdfUrl(blobUrl);
    } catch (err) {
      console.error('Error loading PDF:', err);
      setPdfUrl(null);
    }
  };

  // Download the resume PDF
  const handleDownloadPdf = async () => {
    try {
      const response = await fetch(`${API_URL}/resume/manual/${resumeId}/pdf`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Failed to download PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resumeName || 'Resume'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF');
    }
  };

  // Build context for ChatBot - full on first message, summary on subsequent
  const getChatContext = useCallback(() => {
    if (!chatInitialized) {
      // First message: send full resume for complete context
      return {
        formData: resumeData,
        editorMode: editorMode,
        fullContext: true
      };
    }
    // Subsequent messages: summary only to save tokens
    return {
      formData: {
        contact_info: resumeData?.contact_info,
        experience: (resumeData?.experience || []).map(e => ({
          company_name: e.company_name,
          job_title: e.job_title,
          bullet_count: e.bullets?.length || 0
        })),
        projects: (resumeData?.projects || []).map(p => ({
          project_name: p.project_name,
          bullet_count: p.bullets?.length || 0
        })),
        skills: resumeData?.skills
      },
      editorMode: editorMode,
      fullContext: false
    };
  }, [resumeData, editorMode, chatInitialized]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)' }}>
        <p>Loading resume...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', fontFamily: 'system-ui' }}>
      {/* Header */}
      <div style={{ padding: '20px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <button onClick={() => navigate('/dashboard')} style={{ padding: '8px 16px', background: 'var(--border-light)', color: 'var(--text-primary)', border: 'none', borderRadius: '6px', cursor: 'pointer', marginRight: '12px' }}>
            ← Back to Dashboard
          </button>
          <span style={{ fontSize: '20px', fontWeight: '600' }}>{resumeName}</span>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Editor Mode Toggle */}
          <div style={{ display: 'flex', background: 'var(--bg-surface)', borderRadius: '6px', padding: '2px', border: '1px solid var(--border-light)' }}>
            <button
              onClick={() => handleEditorModeChange('clean')}
              style={{
                padding: '6px 12px',
                background: editorMode === 'clean' ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                border: 'none',
                borderRadius: '4px',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: editorMode === 'clean' ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              Clean Editor
            </button>
            <button
              onClick={() => handleEditorModeChange('raw')}
              style={{
                padding: '6px 12px',
                background: editorMode === 'raw' ? 'rgba(245, 158, 11, 0.3)' : 'transparent',
                border: 'none',
                borderRadius: '4px',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: editorMode === 'raw' ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              Raw LaTeX
            </button>
          </div>
          <button onClick={reloadPDF} style={{ padding: '8px 16px', background: 'rgba(99, 102, 241, 0.2)', color: 'var(--text-primary)', border: '1px solid rgba(99, 102, 241, 0.5)', borderRadius: '6px', cursor: 'pointer' }}>
            Refresh PDF
          </button>
          <button
            onClick={handleDownloadPdf}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600'
            }}
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', height: 'calc(100vh - 80px)' }}>
        {/* Left Panel - Editor */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', borderRight: '1px solid var(--border-light)' }}>

          {/* RAW LATEX EDITOR MODE */}
          {editorMode === 'raw' ? (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'row', gap: '16px' }}>
              {/* LaTeX Editor Column */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ margin: 0 }}>Raw LaTeX Editor</h2>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setShowChatBot(!showChatBot)}
                      style={{
                        padding: '8px 16px',
                        background: showChatBot ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                        border: '1px solid rgba(99, 102, 241, 0.5)',
                        borderRadius: '6px',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: showChatBot ? '600' : '400'
                      }}
                    >
                      {showChatBot ? 'Hide AI Assistant' : 'AI Assistant'}
                    </button>
                    <button
                      onClick={saveLatexContent}
                      disabled={latexSaving || latexLoading}
                      style={{
                        padding: '8px 16px',
                        background: latexSaving ? 'rgba(100, 100, 100, 0.3)' : 'rgba(34, 197, 94, 0.3)',
                        border: `1px solid ${latexSaving ? 'rgba(100, 100, 100, 0.5)' : 'rgba(34, 197, 94, 0.5)'}`,
                        borderRadius: '6px',
                        color: 'var(--text-primary)',
                        cursor: latexSaving || latexLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      {latexSaving ? 'Compiling...' : 'Save & Compile'}
                    </button>
                  </div>
                </div>

              {/* Warning notice */}
              <div style={{
                padding: '12px 16px',
                background: 'rgba(245, 158, 11, 0.15)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                color: 'var(--text-secondary)'
              }}>
                <strong>Advanced Mode:</strong> Edit the raw LaTeX source directly. Changes will be compiled to PDF and synced back to the structured editor. Invalid LaTeX may cause compilation errors.
              </div>

              {/* Error display */}
              {latexError && (
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#ef4444'
                }}>
                  {latexError}
                </div>
              )}

              {/* LaTeX Editor */}
              {latexLoading ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}>
                  Loading LaTeX content...
                </div>
              ) : (
                <textarea
                  value={latexContent}
                  onChange={(e) => setLatexContent(e.target.value)}
                  style={{
                    flex: 1,
                    width: '100%',
                    padding: '16px',
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '8px',
                    color: 'var(--text-primary)',
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    resize: 'none',
                    outline: 'none'
                  }}
                  placeholder="LaTeX content will appear here..."
                  spellCheck={false}
                />
              )}
              </div>

              {/* AI Assistant Panel (ChatBot) */}
              {showChatBot && (
                <div style={{ width: '350px', height: '100%', flexShrink: 0 }}>
                  <ChatBot
                    mode="brainstorming"
                    context={getChatContext()}
                    onClose={() => setShowChatBot(false)}
                  />
                </div>
              )}
            </div>
          ) : (
            /* CLEAN STRUCTURED EDITOR MODE */
            <>
              <h2 style={{ marginBottom: '24px' }}>Resume Content</h2>

              {/* Advanced Formatting Settings */}
              <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-surface)', borderRadius: '8px' }}>
                <div
                  onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <h3 style={{ fontSize: '14px', margin: 0, color: 'var(--text-tertiary)' }}>
                    FORMATTING SETTINGS
                  </h3>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                    {showAdvancedSettings ? '▼' : '▶'}
                  </span>
                </div>

                {showAdvancedSettings && (
                  <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                    {/* Spacing Profile */}
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        Page Density
                      </label>
                      <select
                        value={formatting.spacing_profile}
                        onChange={(e) => updateFormatting({ ...formatting, spacing_profile: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '6px',
                          color: 'var(--text-primary)',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="auto">Auto (based on content)</option>
                        <option value="very_spacious">Very Spacious (fills page)</option>
                        <option value="spacious">Spacious</option>
                        <option value="balanced">Balanced</option>
                        <option value="compact">Compact (fits more content)</option>
                      </select>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                        Controls spacing between sections
                      </div>
                    </div>

                    {/* Font Size */}
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                        Font Size
                      </label>
                      <select
                        value={formatting.font_size}
                        onChange={(e) => updateFormatting({ ...formatting, font_size: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '6px',
                          color: 'var(--text-primary)',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="auto">Auto (based on content)</option>
                        <option value="11pt">11pt (standard)</option>
                        <option value="10.5pt">10.5pt (slightly smaller)</option>
                        <option value="10pt">10pt (compact)</option>
                      </select>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                        Base font size for resume text
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section Visibility Toggles */}
              <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--bg-surface)', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '14px', marginBottom: '12px', color: 'var(--text-tertiary)' }}>VISIBLE SECTIONS</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {Object.keys(visibleSections).map(section => (
                    <button
                      key={section}
                      onClick={() => toggleSection(section)}
                      style={{
                        padding: '6px 12px',
                        background: visibleSections[section] ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                        border: `1px solid ${visibleSections[section] ? 'rgba(34, 197, 94, 0.5)' : 'var(--border-light)'}`,
                        borderRadius: '6px',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        textTransform: 'capitalize'
                      }}
                    >
                      {visibleSections[section] ? '✓' : '○'} {section}
                    </button>
                  ))}
                </div>
              </div>

          {/* Contact Info */}
          {visibleSections.contact && resumeData?.contact_info && (
            <Section
              title="Contact Information"
              expanded={expandedSection === 'contact'}
              onToggle={() => setExpandedSection(expandedSection === 'contact' ? null : 'contact')}
            >
              <Field label="Full Name" value={resumeData.contact_info.full_name} onChange={(v) => updateResumeField('contact_info.full_name', v)} />

              <div style={{ marginTop: '20px', marginBottom: '12px', padding: '12px', background: 'var(--bg-surface)', borderRadius: '6px' }}>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '12px', fontWeight: '600' }}>
                  CONTACT VISIBILITY (toggle to hide from PDF)
                </div>

                <FieldWithVisibility
                  label="Email"
                  value={resumeData.contact_info.email || ''}
                  visible={resumeData.contact_info.visibility?.email !== false}
                  onValueChange={(v) => updateResumeField('contact_info.email', v)}
                  onVisibilityChange={(v) => {
                    const newVisibility = { ...(resumeData.contact_info.visibility || {}), email: v };
                    updateResumeField('contact_info.visibility', newVisibility);
                  }}
                />

                <FieldWithVisibility
                  label="Phone"
                  value={resumeData.contact_info.phone || ''}
                  visible={resumeData.contact_info.visibility?.phone !== false}
                  onValueChange={(v) => updateResumeField('contact_info.phone', v)}
                  onVisibilityChange={(v) => {
                    const newVisibility = { ...(resumeData.contact_info.visibility || {}), phone: v };
                    updateResumeField('contact_info.visibility', newVisibility);
                  }}
                />

                <FieldWithVisibility
                  label="LinkedIn URL"
                  value={resumeData.contact_info.linkedin_url || ''}
                  visible={resumeData.contact_info.visibility?.linkedin !== false}
                  onValueChange={(v) => updateResumeField('contact_info.linkedin_url', v)}
                  onVisibilityChange={(v) => {
                    const newVisibility = { ...(resumeData.contact_info.visibility || {}), linkedin: v };
                    updateResumeField('contact_info.visibility', newVisibility);
                  }}
                />

                <FieldWithVisibility
                  label="GitHub URL"
                  value={resumeData.contact_info.github_url || ''}
                  visible={resumeData.contact_info.visibility?.github !== false}
                  onValueChange={(v) => updateResumeField('contact_info.github_url', v)}
                  onVisibilityChange={(v) => {
                    const newVisibility = { ...(resumeData.contact_info.visibility || {}), github: v };
                    updateResumeField('contact_info.visibility', newVisibility);
                  }}
                />

                <FieldWithVisibility
                  label="Portfolio URL"
                  value={resumeData.contact_info.portfolio_url || ''}
                  visible={resumeData.contact_info.visibility?.portfolio !== false}
                  onValueChange={(v) => updateResumeField('contact_info.portfolio_url', v)}
                  onVisibilityChange={(v) => {
                    const newVisibility = { ...(resumeData.contact_info.visibility || {}), portfolio: v };
                    updateResumeField('contact_info.visibility', newVisibility);
                  }}
                />
              </div>
            </Section>
          )}

          {/* Education */}
          {visibleSections.education && resumeData?.education && (
            <Section
              title="Education"
              expanded={expandedSection === 'education'}
              onToggle={() => setExpandedSection(expandedSection === 'education' ? null : 'education')}
              onAdd={() => addSection('education', 'education')}
            >
              {resumeData.education.map((edu, idx) => (
                <div key={idx} style={{ marginBottom: '20px', padding: '16px', background: 'var(--bg-surface)', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>Education #{idx + 1}</h4>
                    <button onClick={() => deleteSection('education', idx)} style={{ padding: '4px 8px', background: 'rgba(255, 107, 107, 0.2)', border: 'none', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}>
                      🗑 Delete
                    </button>
                  </div>
                  <Field label="School" value={edu.school_name} onChange={(v) => updateResumeField(`education[${idx}].school_name`, v)} />
                  <Field label="Degree" value={edu.degree} onChange={(v) => updateResumeField(`education[${idx}].degree`, v)} />
                  <Field label="Major" value={edu.major} onChange={(v) => updateResumeField(`education[${idx}].major`, v)} />
                  <Field label="Minor" value={edu.minor || ''} onChange={(v) => updateResumeField(`education[${idx}].minor`, v)} />
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Field label="Graduation Date" value={edu.graduation_date} onChange={(v) => updateResumeField(`education[${idx}].graduation_date`, v)} />
                    <Field label="GPA" value={edu.gpa || ''} onChange={(v) => updateResumeField(`education[${idx}].gpa`, v)} />
                  </div>
                  <Field
                    label="Relevant Coursework (comma-separated)"
                    value={(edu.coursework || []).join(', ')}
                    onChange={(v) => updateResumeField(`education[${idx}].coursework`, v.split(',').map(s => s.trim()).filter(s => s))}
                  />
                  <Field
                    label="Honors/Awards (comma-separated)"
                    value={(edu.honors || []).join(', ')}
                    onChange={(v) => updateResumeField(`education[${idx}].honors`, v.split(',').map(s => s.trim()).filter(s => s))}
                  />
                </div>
              ))}
            </Section>
          )}

          {/* Experience */}
          {visibleSections.experience && resumeData?.experience && (
            <Section
              title="Experience"
              expanded={expandedSection === 'experience'}
              onToggle={() => setExpandedSection(expandedSection === 'experience' ? null : 'experience')}
              onAdd={() => addSection('experience', 'experience')}
            >
              {resumeData.experience.map((exp, idx) => (
                <div key={idx} style={{ marginBottom: '20px', padding: '16px', background: 'var(--bg-surface)', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>Experience #{idx + 1}</h4>
                    <button onClick={() => deleteSection('experience', idx)} style={{ padding: '4px 8px', background: 'rgba(255, 107, 107, 0.2)', border: 'none', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}>
                      🗑 Delete
                    </button>
                  </div>
                  <Field label="Company" value={exp.company_name} onChange={(v) => updateResumeField(`experience[${idx}].company_name`, v)} />
                  <Field label="Job Title" value={exp.job_title} onChange={(v) => updateResumeField(`experience[${idx}].job_title`, v)} />
                  <Field label="Location" value={exp.location} onChange={(v) => updateResumeField(`experience[${idx}].location`, v)} />
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Field label="Start Date" value={exp.start_date} onChange={(v) => updateResumeField(`experience[${idx}].start_date`, v)} />
                    <Field label="End Date" value={exp.end_date} onChange={(v) => updateResumeField(`experience[${idx}].end_date`, v)} />
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Bullet Points</label>
                      <button onClick={() => addBulletPoint(`experience[${idx}].bullets`)} style={{ padding: '4px 8px', background: 'rgba(99, 102, 241, 0.2)', border: 'none', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}>
                        + Add Bullet
                      </button>
                    </div>
                    {exp.bullets.map((bullet, bIdx) => (
                      <div key={bIdx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <EditableField
                          value={bullet}
                          onSave={(v) => updateResumeField(`experience[${idx}].bullets[${bIdx}]`, v)}
                          multiline={true}
                          placeholder="Bullet point"
                        />
                        <button onClick={() => deleteBulletPoint(`experience[${idx}].bullets`, bIdx)} style={{ padding: '4px 8px', background: 'rgba(255, 107, 107, 0.2)', border: 'none', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}>
                          🗑
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {/* Projects */}
          {visibleSections.projects && resumeData?.projects && (
            <Section
              title="Projects"
              expanded={expandedSection === 'projects'}
              onToggle={() => setExpandedSection(expandedSection === 'projects' ? null : 'projects')}
              onAdd={() => addSection('projects', 'projects')}
            >
              {resumeData.projects.map((proj, idx) => (
                <div key={idx} style={{ marginBottom: '20px', padding: '16px', background: 'var(--bg-surface)', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', color: 'var(--text-tertiary)' }}>Project #{idx + 1}</h4>
                    <button onClick={() => deleteSection('projects', idx)} style={{ padding: '4px 8px', background: 'rgba(255, 107, 107, 0.2)', border: 'none', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}>
                      🗑 Delete
                    </button>
                  </div>
                  <Field label="Project Name" value={proj.project_name} onChange={(v) => updateResumeField(`projects[${idx}].project_name`, v)} />
                  <Field label="Context" value={proj.context} onChange={(v) => updateResumeField(`projects[${idx}].context`, v)} />
                  <Field label="Date" value={proj.date} onChange={(v) => updateResumeField(`projects[${idx}].date`, v)} />
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Bullet Points</label>
                      <button onClick={() => addBulletPoint(`projects[${idx}].bullets`)} style={{ padding: '4px 8px', background: 'rgba(99, 102, 241, 0.2)', border: 'none', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}>
                        + Add Bullet
                      </button>
                    </div>
                    {proj.bullets.map((bullet, bIdx) => (
                      <div key={bIdx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <EditableField
                          value={bullet}
                          onSave={(v) => updateResumeField(`projects[${idx}].bullets[${bIdx}]`, v)}
                          multiline={true}
                          placeholder="Bullet point"
                        />
                        <button onClick={() => deleteBulletPoint(`projects[${idx}].bullets`, bIdx)} style={{ padding: '4px 8px', background: 'rgba(255, 107, 107, 0.2)', border: 'none', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}>
                          🗑
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {/* Skills */}
          {visibleSections.skills && resumeData?.skills && (
            <Section
              title="Skills"
              expanded={expandedSection === 'skills'}
              onToggle={() => setExpandedSection(expandedSection === 'skills' ? null : 'skills')}
            >
              <Field label="Languages" value={(resumeData.skills.languages || []).join(', ')} onChange={(v) => updateResumeField('skills.languages', v.split(',').map(s => s.trim()))} />
              <Field label="Frontend" value={(resumeData.skills.frontend || []).join(', ')} onChange={(v) => updateResumeField('skills.frontend', v.split(',').map(s => s.trim()))} />
              <Field label="Backend" value={(resumeData.skills.backend || []).join(', ')} onChange={(v) => updateResumeField('skills.backend', v.split(',').map(s => s.trim()))} />
              <Field label="Databases" value={(resumeData.skills.databases || []).join(', ')} onChange={(v) => updateResumeField('skills.databases', v.split(',').map(s => s.trim()))} />
            </Section>
          )}
            </>
          )}
        </div>

        {/* Right Panel - PDF */}
        <div style={{ width: '50%', background: 'var(--bg-elevated)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {pdfUrl ? (
              <iframe src={pdfUrl} title="PDF" style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }} />
            ) : (
              <p style={{ color: 'var(--text-tertiary)' }}>PDF will appear here after first save</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, expanded, onToggle, onAdd, children }) => (
  <div style={{ marginBottom: '20px', border: '1px solid var(--border-light)', borderRadius: '8px', overflow: 'hidden' }}>
    <div
      onClick={onToggle}
      style={{ padding: '16px', background: 'var(--bg-surface)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <h3 style={{ fontSize: '16px', fontWeight: '600' }}>{title}</h3>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {onAdd && (
          <button
            onClick={(e) => { e.stopPropagation(); onAdd(); }}
            style={{ padding: '6px 12px', background: 'rgba(99, 102, 241, 0.3)', border: 'none', borderRadius: '4px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '12px' }}
          >
            + Add
          </button>
        )}
        <span>{expanded ? '▼' : '▶'}</span>
      </div>
    </div>
    {expanded && <div style={{ padding: '16px' }}>{children}</div>}
  </div>
);

const Field = ({ label, value, onChange }) => (
  <div style={{ marginBottom: '12px' }}>
    <label style={{ display: 'block', fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>{label}</label>
    <EditableField value={value || ''} onSave={onChange} placeholder={label} />
  </div>
);

const FieldWithVisibility = ({ label, value, visible, onValueChange, onVisibilityChange }) => (
  <div style={{ marginBottom: '12px' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
      <label style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{label}</label>
      <button
        onClick={() => onVisibilityChange(!visible)}
        style={{
          padding: '4px 10px',
          background: visible ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 107, 107, 0.2)',
          border: `1px solid ${visible ? 'rgba(34, 197, 94, 0.5)' : 'rgba(255, 107, 107, 0.5)'}`,
          borderRadius: '4px',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: '600'
        }}
      >
        {visible ? '👁 Visible' : '🚫 Hidden'}
      </button>
    </div>
    <EditableField
      value={value || ''}
      onSave={onValueChange}
      placeholder={visible ? label : `${label} (hidden from PDF)`}
    />
  </div>
);

export default ManualResumeEditor;
